/**
 * Business Concerns Card
 * =======================
 * Displays stocktake issues, discrepancies, anomalies, and risk areas
 * in a compact, information-dense format for dashboard use.
 * Uses industry-specific concerns data for relevance.
 */

import React, { useState, useMemo } from 'react';
import { toast } from '@/utils/safeToast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package, AlertTriangle, TrendingDown, ShieldAlert, ChevronRight,
  Clock, DollarSign, Eye, CheckCircle2, X, Database, Info, Activity,
  FileText, BarChart3
} from 'lucide-react';
import { getIndustryConcerns } from '@/config/industrySpecificConcerns';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/contexts/ClientContext';
import { useNavigate } from 'react-router-dom';
import Portal from '@/components/common/Portal';

const severityStyles = {
  high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-500' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-500' },
  low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', badge: 'bg-blue-500' },
};

// ── Metric formatting ─────────────────────────────────────────────
// Stocktake metrics mix currency (MRR, costs, budgets) and counts (units,
// subscriptions, hours). We render thousands separators on everything and a
// "$" prefix only for currency-magnitude metrics (>= 1000 reads as dollars,
// small integers read as counts) — keeps the two consistent within a row.
const formatThousands = (n) => Number(n).toLocaleString('en-AU');

const isCurrencyMetric = (item) =>
  Math.max(Math.abs(Number(item?.expected) || 0), Math.abs(Number(item?.actual) || 0)) >= 1000;

const formatMetricValue = (item, n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return n;
  const num = Number(n);
  const body = formatThousands(Math.abs(num));
  const sign = num < 0 ? '-' : '';
  return isCurrencyMetric(item) ? `${sign}$${body}` : `${sign}${body}`;
};

const formatVariance = (item) => {
  const v = Number(item?.variance);
  if (isNaN(v)) return item?.variance;
  const body = formatThousands(Math.abs(v));
  const cur = isCurrencyMetric(item);
  if (v < 0) return cur ? `-$${body}` : `-${body}`;
  return cur ? `+$${body}` : `+${body}`;
};

// Data source mapping for different concern types
const DATA_SOURCES = {
  stocktake: {
    primary: 'POS System',
    secondary: 'Inventory Management',
    description: 'Stock variance detected by comparing POS sales data with inventory counts',
    systems: ['Lightspeed POS', 'Inventory Manager', 'Manual Stocktake'],
    lastSync: '5 minutes ago',
  },
  discrepancies: {
    primary: 'Multi-System Analysis',
    secondary: 'AI Reconciliation',
    description: 'Discrepancy identified through cross-system data validation',
    systems: ['POS System', 'Delivery Records', 'Wastage Logs', 'Cash Drawer'],
    lastSync: '2 minutes ago',
  },
  anomalies: {
    primary: 'Corvana AI',
    secondary: 'Pattern Analysis',
    description: 'AI detected unusual pattern compared to historical baseline',
    systems: ['Financial Data', 'Operational Metrics', 'Industry Benchmarks'],
    lastSync: 'Real-time',
  },
  riskAreas: {
    primary: 'Compliance Engine',
    secondary: 'Regulatory Database',
    description: 'Risk flagged based on compliance requirements and deadlines',
    systems: ['Compliance Calendar', 'Certification Database', 'Regulatory Updates'],
    lastSync: 'Daily',
  },
};

// Detail Panel Component
const DetailPanel = ({ item, type, onClose }) => {
  const source = DATA_SOURCES[type];
  
  if (!item || !source) return null;
  
  return (
    <Portal><div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div 
        className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Concern Details</h3>
            <p className="text-sm text-muted-foreground">{type.charAt(0).toUpperCase() + type.slice(1)}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Issue Summary */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border">
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${severityStyles[item.severity]?.bg || 'bg-gray-100'}`}>
                {type === 'stocktake' && <Package className="w-5 h-5 text-red-600" />}
                {type === 'discrepancies' && <AlertTriangle className="w-5 h-5 text-amber-600" />}
                {type === 'anomalies' && <TrendingDown className="w-5 h-5 text-blue-600" />}
                {type === 'riskAreas' && <ShieldAlert className="w-5 h-5 text-purple-600" />}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">
                  {item.item || item.type || item.area || 'Issue'}
                </h4>
                <Badge className={`mt-1 text-xs ${severityStyles[item.severity]?.badge || 'bg-gray-500'} text-white`}>
                  {item.severity} priority
                </Badge>
              </div>
            </div>
            <p className="text-sm text-gray-700">
              {item.desc || item.issue || `Expected: ${formatMetricValue(item, item.expected)}, Actual: ${formatMetricValue(item, item.actual)}, Variance: ${formatVariance(item)}`}
            </p>
            {item.value && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <span className="text-sm text-gray-500">Financial Impact: </span>
                <span className="font-semibold text-gray-900">{item.value}</span>
              </div>
            )}
          </div>
          
          {/* Data Sources Section */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Database className="w-4 h-4 text-primary" />
              Data Sources
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800">{source.primary}</p>
                  <p className="text-xs text-emerald-600">{source.secondary}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <Info className="w-4 h-4 inline mr-2 text-blue-500" />
                {source.description}
              </p>
            </div>
          </div>
          
          {/* Systems Involved */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              Systems Checked
            </h4>
            <div className="flex flex-wrap gap-2">
              {source.systems.map((sys, idx) => (
                <Badge key={`${sys?.id ?? sys?.name ?? sys?.title ?? 'sys'}-${idx}`} variant="outline" className="text-xs">
                  {sys}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Last synced: {source.lastSync}
            </p>
          </div>
          
          {/* Recommended Actions */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Recommended Actions
            </h4>
            <div className="space-y-2">
              {type === 'stocktake' && (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Conduct physical count verification
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Review POS void/refund transactions
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Check for delivery discrepancies
                  </div>
                </>
              )}
              {type === 'discrepancies' && (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Cross-reference with delivery dockets
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Review shift handover notes
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Contact supplier if shortage confirmed
                  </div>
                </>
              )}
              {type === 'anomalies' && (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Review trend data for past 30 days
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Compare against industry benchmarks
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Investigate root cause factors
                  </div>
                </>
              )}
              {type === 'riskAreas' && (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    {item.action || 'Take immediate action'}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Document resolution in compliance log
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Set reminder for follow-up
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={() => { toast.success('Concern marked as resolved'); onClose(); }}>
              Mark as Resolved
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => { toast.info('Concern escalated to management'); onClose(); }}>
              Escalate
            </Button>
          </div>
        </div>
      </div>
    </div></Portal>
  );
};

// Unified Card Row Component - Same design for all types
const UnifiedConcernRow = ({ item, type, icon: Icon, onAction }) => {
  const style = severityStyles[item.severity];
  
  // Get the title based on type
  const getTitle = () => {
    if (type === 'stocktake') return item.item;
    if (type === 'discrepancies') return item.type;
    if (type === 'anomalies') return item.type;
    if (type === 'riskAreas') return item.area;
    return 'Issue';
  };
  
  // Get the description based on type
  const getDescription = () => {
    if (type === 'stocktake') return `Expected: ${formatMetricValue(item, item.expected)} | Actual: ${formatMetricValue(item, item.actual)}`;
    if (type === 'discrepancies') return item.desc;
    if (type === 'anomalies') return item.desc;
    if (type === 'riskAreas') return item.issue;
    return '';
  };
  
  // Get the badge text
  const getBadge = () => {
    if (type === 'stocktake') {
      const isShortage = item.variance < 0;
      return { text: formatVariance(item), variant: isShortage ? 'destructive' : 'success' };
    }
    if (type === 'discrepancies') return { text: item.gap, variant: 'default' };
    if (type === 'anomalies') return { text: item.severity, variant: 'default' };
    if (type === 'riskAreas') return { text: item.severity, variant: 'default' };
    return { text: '', variant: 'default' };
  };
  
  // Get the value/impact
  const getValue = () => {
    if (type === 'stocktake') return item.value;
    if (type === 'discrepancies') return item.value;
    if (type === 'anomalies') return `Detected: ${item.detected}`;
    if (type === 'riskAreas') return item.action;
    return '';
  };
  
  // Get button text
  const getButtonText = () => {
    if (type === 'stocktake') return 'View';
    if (type === 'discrepancies') return 'Investigate';
    if (type === 'anomalies') return 'Review';
    if (type === 'riskAreas') return 'Take Action';
    return 'View';
  };

  const badge = getBadge();
  const isStockShortage = type === 'stocktake' && item.variance < 0;
  
  return (
    <div className={`p-3 rounded-lg ${style.bg} border ${style.border} group h-[88px] flex flex-col justify-between`}>
      {/* Top Row: Icon, Title, Badge */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center flex-shrink-0">
          <Icon className={`w-4 h-4 ${style.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase truncate">{getTitle()}</span>
            <Badge 
              className={`text-[10px] ${style.badge} text-white flex-shrink-0`}
            >
              {badge.text}
            </Badge>
          </div>
          <p className="text-sm text-gray-800 line-clamp-1 mt-0.5">{getDescription()}</p>
        </div>
      </div>
      
      {/* Bottom Row: Value/Info and Action Button */}
      <div className="flex items-center justify-between mt-2 pl-11">
        <span className={`text-xs font-medium flex items-center gap-1 ${
          type === 'stocktake' ? (isStockShortage ? 'text-red-600' : 'text-emerald-600') :
          type === 'discrepancies' ? 'text-red-600' :
          type === 'anomalies' ? 'text-gray-500' :
          'text-blue-600'
        }`}>
          {type === 'anomalies' ? (
            <><Clock className="w-3 h-3" /> {getValue()}</>
          ) : type === 'riskAreas' ? (
            <><CheckCircle2 className="w-3 h-3" /> {getValue()}</>
          ) : (
            getValue()
          )}
        </span>
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-6 px-2 text-xs"
          onClick={() => onAction?.(item, type)}
        >
          {getButtonText()} <ChevronRight className="w-3 h-3 ml-0.5" />
        </Button>
      </div>
    </div>
  );
};

// Main Component
const BusinessConcernsCard = ({ 
  concerns = null,
  className = '',
  compact = false,
  maxItems = 4,
  onItemAction = () => {},
  industry: propIndustry = null,
  isDemo: propIsDemo = false
}) => {
  const [activeTab, setActiveTab] = useState('stocktake');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  
  // Get industry for industry-specific concerns - prop > clientContext > user > default
  const { user} = useAuth();
  const { customerBusiness } = useClient();
  const navigate = useNavigate();
  const userIndustry = propIndustry || customerBusiness?.industry?.toLowerCase() || user?.industry || user?.industry_category || 'default';
  const isDemo = propIsDemo || user?.is_demo === true;
  const businessName = customerBusiness?.name || 'your business';

  // Get industry-specific concerns data (demo showcase only)
  const industryConcerns = useMemo(() => getIndustryConcerns(userIndustry), [userIndustry]);

  // Live concerns must be explicitly provided by the caller. Demo accounts see the
  // curated industry showcase; REAL accounts NEVER see fabricated concern numbers —
  // when there's no live feed they get a connect-to-unlock state instead.
  const data = concerns || (isDemo ? industryConcerns : null);

  // Count totals for badge
  const totalConcerns = {
    stocktake: (data?.stocktake || []).length,
    discrepancies: (data?.discrepancies || []).length,
    anomalies: (data?.anomalies || []).length,
    riskAreas: (data?.riskAreas || []).length,
  };
  
  const totalCount = Object.values(totalConcerns).reduce((a, b) => a + b, 0);
  const hasData = totalCount > 0;
  
  // Handler for opening detail panel
  const handleItemClick = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
  };
  
  // Handler for closing detail panel
  const handleClosePanel = () => {
    setSelectedItem(null);
    setSelectedType(null);
  };

  // Real (non-demo) accounts with no live concern feed: never show fabricated
  // numbers — prompt to connect a data source + promise of benchmarking.
  if (!hasData) {
    if (compact) {
      return (
        <div className={`glass rounded-xl p-3 ${className}`} data-testid="business-concerns-compact">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Business Concerns
            </h4>
          </div>
          <p className="text-xs text-gray-500 mb-3" data-testid="business-concerns-empty">
            Connect your data to surface stock issues, gaps, anomalies and risks.
          </p>
          <Button size="sm" variant="outline" className="w-full text-xs h-8" onClick={() => navigate('/app/integrations')} data-testid="connect-concerns-data-btn">
            <Database className="w-3.5 h-3.5 mr-1.5" /> Connect a data source
          </Button>
        </div>
      );
    }
    return (
      <div className={`glass rounded-2xl p-4 ${className}`} data-testid="business-concerns-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Business Concerns
          </h3>
        </div>
        <div className="text-center py-8 px-4" data-testid="business-concerns-empty">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
            <Database className="w-6 h-6 text-amber-500" />
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Connect your data to surface concerns</h4>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-4">
            Business Concerns highlights stock issues, gaps, anomalies and risks for {businessName} from your live data.
            Connect your accounting, POS or operations source to unlock it — until then we benchmark you against industry standards.
          </p>
          <Button size="sm" onClick={() => navigate('/app/integrations')} className="bg-primary hover:bg-primary/90" data-testid="connect-concerns-data-btn">
            <Database className="w-4 h-4 mr-2" /> Connect a data source
          </Button>
        </div>
      </div>
    );
  }

  if (compact) {
    // Compact view - shows summary only
    return (
      <div className={`glass rounded-xl p-3 ${className}`} data-testid="business-concerns-compact">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Business Concerns
          </h4>
          <Badge variant="destructive" className="text-xs">{totalCount}</Badge>
        </div>
 <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-200">
            <Package className="w-4 h-4 text-red-500" />
            <div>
              <p className="text-lg font-bold text-red-700">{totalConcerns.stocktake}</p>
              <p className="text-[10px] text-red-600">Stock Issues</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
            <TrendingDown className="w-4 h-4 text-amber-500" />
            <div>
              <p className="text-lg font-bold text-amber-700">{totalConcerns.discrepancies}</p>
              <p className="text-[10px] text-amber-600">Discrepancies</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`glass rounded-2xl p-4 ${className}`} data-testid="business-concerns-card">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Business Concerns
            <Badge variant="destructive" className="text-xs">{totalCount} items</Badge>
          </h3>
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => toast.info(`Showing all ${totalCount} business concerns...`)}>
            View All <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex justify-start overflow-x-auto scrollbar-hide [&>*]:flex-shrink-0 md:grid md:grid-cols-4 h-9 mb-3 bg-gray-100/80">
            <TabsTrigger value="stocktake" className="text-xs gap-1.5 py-1.5 data-[state=active]:bg-white">
              <Package className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Stock</span>
              {totalConcerns.stocktake > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-red-500">
                  {totalConcerns.stocktake}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="discrepancies" className="text-xs gap-1.5 py-1.5 data-[state=active]:bg-white">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Gaps</span>
              {totalConcerns.discrepancies > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-amber-500">
                  {totalConcerns.discrepancies}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="anomalies" className="text-xs gap-1.5 py-1.5 data-[state=active]:bg-white">
              <TrendingDown className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Anomaly</span>
              {totalConcerns.anomalies > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-blue-500">
                  {totalConcerns.anomalies}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="risks" className="text-xs gap-1.5 py-1.5 data-[state=active]:bg-white">
              <ShieldAlert className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Risk</span>
              {totalConcerns.riskAreas > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-purple-500">
                  {totalConcerns.riskAreas}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stocktake" className="space-y-2 mt-0">
            {(data.stocktake || []).slice(0, maxItems).map((item) => (
              <UnifiedConcernRow 
                key={item.id} 
                item={item} 
                type="stocktake"
                icon={Package}
                onAction={handleItemClick} 
              />
            ))}
            {(!data.stocktake || data.stocktake.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No stocktake issues</p>
            )}
          </TabsContent>

          <TabsContent value="discrepancies" className="space-y-2 mt-0">
            {(data.discrepancies || []).slice(0, maxItems).map((item) => (
              <UnifiedConcernRow 
                key={item.id} 
                item={item} 
                type="discrepancies"
                icon={AlertTriangle}
                onAction={handleItemClick} 
              />
            ))}
            {(!data.discrepancies || data.discrepancies.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No discrepancies detected</p>
            )}
          </TabsContent>

          <TabsContent value="anomalies" className="space-y-2 mt-0">
            {(data.anomalies || []).slice(0, maxItems).map((item) => (
              <UnifiedConcernRow 
                key={item.id} 
                item={item} 
                type="anomalies"
                icon={TrendingDown}
                onAction={handleItemClick} 
              />
            ))}
            {(!data.anomalies || data.anomalies.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No anomalies detected</p>
            )}
          </TabsContent>

          <TabsContent value="risks" className="space-y-2 mt-0">
            {(data.riskAreas || []).slice(0, maxItems).map((item) => (
              <UnifiedConcernRow 
                key={item.id} 
                item={item} 
                type="riskAreas"
                icon={ShieldAlert}
                onAction={handleItemClick} 
              />
            ))}
            {(!data.riskAreas || data.riskAreas.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No risk areas flagged</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Detail Panel */}
      {selectedItem && (
        <DetailPanel 
          item={selectedItem} 
          type={selectedType} 
          onClose={handleClosePanel} 
        />
      )}
    </>
  );
};

export default BusinessConcernsCard;
